import React, { useEffect, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { useBusinessCompliance } from "contexts/business-compliance-context";
import { useUpdateQueryParams } from "hooks/useUpdateQueryParams";
import { useSearchParams } from "react-router-dom";
import {
  TDateRangeFilterTypes,
  getRandomNumber,
  parseCustomDateFilterRangeString,
} from "utils";
import { INDEX_OFFSET, ZERO } from "utils/constants";
import { Maybe } from "utils/general/maybe";

import {
  DEFAULT_FILTER_TEXT,
  DateFilter,
  NORMAL_DATE_FILTERS,
} from "ui/date-filter";
import { TDateFilterOption } from "ui/date-filter/types";
import { IDropDownRef } from "ui/dropdown/types";
import { IFilterRef } from "ui/filter/types";
import { Loader } from "ui/loader";
import { Heading4, Paragraph3, Paragraph4 } from "ui/typography";

import { COLORS, COLORS_MAX_LENGTH_INDEX } from "../consts";
import { BusinessComplianceTabItem } from "../tab-item";
import { SelectedBusinessComplianceTabItem } from "../tab-item/SelectedBusinessComplianceTabItem";
import { NoBusinesses } from "./NoBusinesses";
import { IBusinessComplianceTabItemsProps } from "./types";

const START_INDEX = 0;
const lastColor: string | null = null;

const options: TDateFilterOption[] = [
  {
    type: "regular",
    key: "allTime",
    title: "All",
  },
  {
    type: "regular",
    key: "today",
    title: "Today",
  },
  {
    type: "regular",
    key: "yesterday",
    title: "Yesterday",
  },
  {
    type: "regular",
    key: "oldestToNewest",
    title: "Oldest to Newest",
  },
  {
    type: "regular",
    key: "newestToOldest",
    title: "Newest to Oldest",
  },
  {
    type: "regular",
    key: "thisMonth",
    title: "This Month",
  },
  {
    type: "regular",
    key: "lastMonth",
    title: "Last Month",
  },
  {
    type: "date-range",
    key: "dateRange",
    title: "Custom Date Range",
  },
];

const statsDateFilterRangeValues: Record<string, string> = {
  allTime: "all",
  today: "today",
  yesterday: "yesterday",
  oldestToNewest: "oldest_to_newest",
  newestToOldest: "newest_to_oldest",
  thisMonth: "this_month",
  lastMonth: "last_month",
};

const dateFilterValueToTextMap: Record<string, string> = {
  [DEFAULT_FILTER_TEXT]: DEFAULT_FILTER_TEXT,
  allTime: "All",
  today: "Today",
  yesterday: "Yesterday",
  oldestToNewest: "Oldest to Newest",
  newestToOldest: "Newest to Oldest",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  dateRange: "Custom Date Range",
};

const getKeyByValue = (value: string) =>
  Object.entries(statsDateFilterRangeValues).find(([, v]) => v === value)?.[
    ZERO
  ];

export const getNextColor = (
  index: number,
  lastColor: string | null
): string => {
  const availableColors = COLORS.filter((color) => color !== lastColor);
  const colorIndex = index % availableColors.length;
  const selectedColor = availableColors[colorIndex];

  lastColor = selectedColor;
  return selectedColor;
};

export const BusinessComplianceTabItems = (
  props: IBusinessComplianceTabItemsProps
) => {
  const {
    businesses,
    allBusinesses,
    selectedBusiness,
    handleSelectBusiness,
    status,
    searchQuery,
    setSearchQuery,
  } = props;
  const { isFetchingBusinesses } = useBusinessCompliance();
  const updateQueryParams = useUpdateQueryParams();
  const [searchParams] = useSearchParams();
  const dateRange = searchParams.get("dateRange");
  const startDate = searchParams.get("startDate");
  const endDate = searchParams.get("endDate");
  const [isLoading, setIsLoading] = useState(false);
  let dateFilterDataKey = DEFAULT_FILTER_TEXT;
  let dateFilterDataValue = DEFAULT_FILTER_TEXT;

  if (startDate) {
    dateFilterDataKey = "dateRange";
    dateFilterDataValue = `startDate=${startDate}&endDate=${endDate}`;
  } else if (dateRange) {
    dateFilterDataValue = getKeyByValue(dateRange) || DEFAULT_FILTER_TEXT;
  }
  const [dateFilterValue, setDateFilterValue] = useState<
    Record<string, string>
  >({
    [dateFilterDataKey]: dateFilterDataValue,
  });
  const dropDownPopUpRef = useRef<IDropDownRef>(null);
  const customDateRangeFilterRef = useRef<IFilterRef>(null);

  const handleApplyFilter = () => {
    setIsLoading(true);
    const [value] = Object.values(dateFilterValue);
    const filterPayload = parseCustomDateFilterRangeString(value);
    updateQueryParams({
      ...filterPayload,
      isDateFilter: "true",
    });
    setIsLoading(false);
    if (customDateRangeFilterRef.current) {
      customDateRangeFilterRef.current.handleToggleFilter();
    }
  };

  useEffect(() => {
    const [value] = Object.values(dateFilterValue);
    if (value === DEFAULT_FILTER_TEXT) return;
    const filter = value as TDateRangeFilterTypes;
    if (filter === "allTime") {
      updateQueryParams({});
      return;
    }

    if (NORMAL_DATE_FILTERS.includes(filter)) {
      const payload: Record<string, string | null> = {
        dateRange: statsDateFilterRangeValues[value],
        orderBy: null,
        isDateFilter: "true",
      };
      updateQueryParams(payload);
      return;
    }
  }, [dateFilterValue]);

  return (
    <div className="flex flex-col gap-2">
      <Box className="flex justify-between items-center px-4">
        {status === "review" && (
          <Box className="h-6 flex items-center justify-start">
            <Paragraph4
              lineHeight="24px"
              textColor="#374151"
              fontWeight="500"
            >
              Next up
            </Paragraph4>
          </Box>
        )}
        <Box className="flex items-center ml-auto">
          <DateFilter
            options={options}
            dateFilterValue={dateFilterValue}
            setDateFilterValue={setDateFilterValue}
            onApplyFilter={handleApplyFilter}
            dateFilterValueToTextMap={dateFilterValueToTextMap}
            dropDownPopUpRef={dropDownPopUpRef}
            customDateRangeFilterRef={customDateRangeFilterRef}
            isLoading={isLoading}
            customFilterButtonCss={{
              height: "28px",
              borderRadius: "8px",
              boxShadow: "0px 2px 6px 0px #122B690A",
              padding: "8px",
              color: "#4A5564",
              border: "1px solid #D3D2DA",
              fontSize: "12px",
              fontWeight: "500",
            }}
          />
        </Box>
      </Box>
      <Maybe condition={isFetchingBusinesses}>
        <Box className="h-16 flex items-center justify-center">
          <Loader hideLoadingText />
        </Box>
      </Maybe>
      <Maybe condition={!isFetchingBusinesses}>
        {allBusinesses.length > START_INDEX ? (
          <>
            {selectedBusiness && !searchQuery && (
              <div className="px-4">
                <SelectedBusinessComplianceTabItem
                  business={selectedBusiness}
                  initialsBgColor={getNextColor(
                    getRandomNumber(START_INDEX, COLORS_MAX_LENGTH_INDEX),
                    lastColor
                  )}
                  handleSelectBusiness={handleSelectBusiness}
                  isSelected
                />
              </div>
            )}
            <div
              className={`flex flex-col gap-1 overflow-auto px-4 ${selectedBusiness ? "h-[calc(100vh-500px)]" : "h-[calc(100vh-440px)]"}`}
            >
              {searchQuery && businesses.length === START_INDEX ? (
                <Box className="h-[78px] mt-[90px] flex flex-col gap-2 items-center w-full max-w-[260px]">
                  <Heading4
                    fontWeight="700"
                    textColor="#1A202C"
                    lineHeight="25.6px"
                  >
                    Search Not Found
                  </Heading4>
                  <Paragraph3
                    lineHeight="22.4px"
                    textColor="#718096"
                    className="text-center"
                  >
                    We couldn't find what you were searching for. Try something
                    else
                  </Paragraph3>
                </Box>
              ) : (
                businesses
                  .filter(({ _id }) => selectedBusiness?.business?.id !== _id)
                  .map((business, index) => {
                    const businessIndexedLength =
                      businesses.length - INDEX_OFFSET;
                    const isLastItem = index === businessIndexedLength;
                    if (isLastItem) {
                      return (
                        <Box key={index}>
                          <BusinessComplianceTabItem
                            business={business}
                            initialsBgColor={getNextColor(index, lastColor)}
                            handleSelectBusiness={handleSelectBusiness}
                          />
                        </Box>
                      );
                    }

                    return (
                      <BusinessComplianceTabItem
                        key={index}
                        business={business}
                        initialsBgColor={getNextColor(index, lastColor)}
                        handleSelectBusiness={handleSelectBusiness}
                      />
                    );
                  })
              )}
            </div>
          </>
        ) : (
          <NoBusinesses setSearchQuery={setSearchQuery} />
        )}
      </Maybe>
    </div>
  );
};
